import { userFacingError } from "../../userFacingError";
import { geminiUsage } from "../googleGemini/usage";
import { MODELS_HELPER_TEXT, MODELS_PREFIX } from "../models";
import { customAuth, oauthAuth } from "../standardAuthPatterns";
import { defineProduct, productVariant } from "../types";
import { escapeRegexLiteral } from "../urlPatternConversion";
import logo from "./logo.svg";

const KEYWORDS =
  "gcp googleapis platform iam service account workspace delegation";
// www.googleapis.com also serves OAuth, so only the Workspace API paths on that host get this credential.
const defaultUrlPattern = () =>
  "https://(?:w*[^w/.][^/]*\\.googleapis\\.com/.*|www\\.googleapis\\.com/(?:(?:(?:upload|download)/)?(?:drive|gmail|groups)|calendar|tasks|webmasters|admin|apps/licensing|batch)/.*)";

const serviceAccountConnectionTest = {
  protocol: "http",
  request: () => ({
    url: "https://cloudresourcemanager.googleapis.com/v1/projects",
    expectedStatus: [200, 403],
  }),
} as const;

const serviceAccountKeyItem = {
  kind: "input",
  label: "Service account key (JSON)",
  placeholder: '{"type": "service_account", "project_id": "...", ...}',
  required: true,
  target: { kind: "secrets", field: "serviceAccountKey" },
  helperText: "Paste the entire JSON key file contents.",
} as const;

const serviceAccountKeyVariant = productVariant({
  identifier: "googleCloud",
  authLabel: "Service account key",
  keywords: KEYWORDS,
  protocol: "http",
  defaultUrlPattern,
  auth: customAuth("googleCloud"),
  steps: [
    {
      title: "Create a service account",
      items: [
        {
          kind: "markdown",
          content:
            "Visit the [Google Cloud Console](https://console.cloud.google.com/iam-admin/serviceaccounts) and create a new service account, or use an existing one.\n\nGrant the service account the necessary roles for the APIs you want to use.",
        },
      ],
    },
    {
      title: "Generate a service account key",
      items: [
        {
          kind: "markdown",
          content:
            "In the service account details, go to the **Keys** tab and click **Add Key** → **Create new key**. Choose **JSON** format.\n\nThe key file will be downloaded automatically. Open it and copy the entire JSON contents.",
        },
      ],
    },
    { title: "Enter your service account key", items: [serviceAccountKeyItem] },
    {
      title: "Domain-wide delegation",
      items: [
        {
          kind: "select",
          label: "User delegation",
          target: { kind: "configuration", field: "delegationMode" },
          options: [
            { value: "", label: "None" },
            { value: "static", label: "Specific user" },
            { value: "dynamic", label: "Dynamic (set per-request)" },
          ],
          defaultValue: "",
          helperText:
            "Impersonate a Google Workspace user via domain-wide delegation.",
        },
        {
          kind: "input",
          label: "Delegation email",
          placeholder: "user@example.com",
          target: { kind: "configuration", field: "delegationSubject" },
          helperText: "The email of the user to impersonate.",
          visibleWhen: [
            {
              target: { kind: "configuration", field: "delegationMode" },
              value: "static",
            },
          ],
        },
        {
          kind: "input",
          label: "Scopes",
          placeholder: "https://www.googleapis.com/auth/gmail.readonly",
          target: { kind: "configuration", field: "scope" },
          helperText:
            "Space-separated OAuth scopes. Must match the scopes granted in the Google Workspace Admin Console.",
          visibleWhen: [
            {
              target: { kind: "configuration", field: "delegationMode" },
            },
          ],
        },
      ],
    },
  ],
  connectionTest: serviceAccountConnectionTest,
  usageHint: ({ delegationMode }) => {
    if (delegationMode !== "dynamic") return undefined;
    return "This connector uses domain-wide delegation with dynamic user impersonation. To set which user to act as, include the header x-3b-delegation-subject with their email (e.g. user@company.com).";
  },
  publicEnvVars: ({ delegationMode, delegationSubject, scope }) => {
    const mode = delegationMode;
    if (!mode) return {} as Record<string, string>;
    const vars: Record<string, string> = {};
    if (mode === "static" && delegationSubject) {
      vars.GCP_DELEGATION_SUBJECT = delegationSubject;
    }
    if (scope) {
      vars.GCP_DELEGATION_SCOPE = scope;
    }
    return vars;
  },
});

const vertexHost =
  "(?:(?:[a-z0-9-]+-)?aiplatform\\.googleapis\\.com|aiplatform\\.(?:us|eu)\\.rep\\.googleapis\\.com)";
const vertexModelPath =
  "projects/[a-z0-9-]+/locations/[a-z0-9-]+/publishers/google";
const vertexGenerationPath =
  "(?:/models/[^/?#:]+:(?:generateContent|streamGenerateContent)(?:\\?.*)?)?";
const vertexUrlPattern = `https://${vertexHost}/v1beta1/(?:publishers/google/models\\?pageSize=1|${vertexModelPath}${vertexGenerationPath})$`;

const vertexAiVariant = productVariant({
  identifier: "googleCloudVertexAi",
  authLabel: "Vertex AI (service account key)",
  protocol: "http",
  defaultUrlPattern: () => vertexUrlPattern,
  auth: customAuth("googleCloud"),
  steps: [
    {
      title: "Connect Vertex AI",
      items: [
        {
          kind: "markdown",
          content:
            "Create a service account in the [Google Cloud Console](https://console.cloud.google.com/iam-admin/serviceaccounts), grant it access to Vertex AI, and generate a JSON key.",
        },
        serviceAccountKeyItem,
        {
          kind: "list",
          label: "Available models",
          prefix: MODELS_PREFIX,
          columns: [{ field: "name", placeholder: "gemini-2.5-pro" }],
          addLabel: "Add model",
          helperText: MODELS_HELPER_TEXT,
        },
      ],
    },
  ],
  connectionTest: {
    protocol: "http",
    request: () => ({
      url: "https://aiplatform.googleapis.com/v1beta1/publishers/google/models?pageSize=1",
      // Model Garden listing can require permissions unrelated to inference.
      expectedStatus: [200, 403],
    }),
  },
  usage: geminiUsage(),
});

const federationVariant = productVariant({
  identifier: "googleCloudFederation",
  authLabel: "Workload identity federation (keyless)",
  keywords: KEYWORDS,
  protocol: "http",
  defaultUrlPattern,
  auth: (app) =>
    customAuth("googleCloudFederation", {
      issuer: app.issuer ?? "",
      projectNumber: app.config.projectNumber,
      poolId: app.config.poolId,
      providerId: app.config.providerId,
      serviceAccountEmail: app.config.serviceAccountEmail,
      scope: app.config.scope,
    }),
  steps: [
    {
      title: "Create a workload identity pool and provider",
      items: [
        {
          kind: "markdown",
          content:
            "Open the [Workload Identity Federation page](https://console.cloud.google.com/iam-admin/workload-identity-pools), click **Create pool**, and name the pool. Then add a provider of type **OpenID Connect (OIDC)**. For **Issuer (URL)**, enter:",
        },
        {
          kind: "copyable",
          label: "Issuer URL",
          value: "{OIDC_PROVIDER_URL}",
        },
        {
          kind: "markdown",
          content:
            "Skip the JWK file — Google fetches the keys from the issuer. Under **Audiences**, choose **Allowed audiences** and enter:",
        },
        {
          kind: "copyable",
          label: "Allowed audience",
          value: "connected-app:{CONNECTED_APP_ID}",
        },
        {
          kind: "markdown",
          content:
            "Under **Attribute mapping**, map **google.subject** to the value below, then create the provider:",
        },
        {
          kind: "copyable",
          label: "google.subject",
          value: "assertion.sub",
        },
      ],
    },
    {
      title: "Enter the provider details",
      items: [
        {
          kind: "input",
          label: "Project number",
          placeholder: "458876884405",
          required: true,
          target: { kind: "configuration", field: "projectNumber" },
          helperText:
            "The numeric ID in the pool page’s IAM principal (projects/<number>/…), also shown on the console’s project dashboard.",
        },
        {
          kind: "input",
          label: "Pool ID",
          placeholder: "my-pool",
          required: true,
          target: { kind: "configuration", field: "poolId" },
          helperText:
            "Open the pool’s details page and copy the value labeled “ID”. Google usually derives it from the pool name.",
        },
        {
          kind: "input",
          label: "Provider ID",
          placeholder: "3b",
          required: true,
          target: { kind: "configuration", field: "providerId" },
          helperText:
            "Open the provider’s details and copy the value labeled “ID”. Google usually derives it from the provider name.",
        },
      ],
    },
    {
      title: "Grant access to a service account (optional)",
      items: [
        {
          kind: "markdown",
          content:
            "By default, requests act as the federated identity itself — grant IAM roles directly to the pool principal and leave the fields below blank.\n\nTo act as a service account instead: create or pick a [service account](https://console.cloud.google.com/iam-admin/serviceaccounts) with the roles you need, then on the pool’s page click **Grant access**, choose **Grant access using service account impersonation**, select the service account, and under the principal filter choose the **subject** attribute with this value:",
        },
        {
          kind: "copyable",
          label: "Subject",
          value: "connected-app:{CONNECTED_APP_ID}",
        },
        {
          kind: "markdown",
          content:
            "Dismiss the “Configure your application” dialog Google offers afterwards — 3B doesn’t use a credential file. Then enter the service account’s email:",
        },
        {
          kind: "input",
          label: "Service account email (optional)",
          placeholder: "my-service-account@my-project.iam.gserviceaccount.com",
          target: { kind: "configuration", field: "serviceAccountEmail" },
          helperText:
            "Leave blank to use the federated identity directly, without impersonation.",
        },
        {
          kind: "input",
          label: "Scopes (optional)",
          placeholder: "https://www.googleapis.com/auth/cloud-platform",
          target: { kind: "configuration", field: "scope" },
          helperText:
            "Space-separated OAuth scopes for the access token. Defaults to https://www.googleapis.com/auth/cloud-platform.",
        },
      ],
    },
  ],
  connectionTest: {
    protocol: "http",
    request: () => "https://sts.googleapis.com/.well-known/3b-connection-test",
  },
  usageHint: () =>
    "Requests to googleapis.com hosts are authenticated automatically with short-lived Google Cloud credentials. Make plain HTTPS calls to the APIs — no local credentials or GOOGLE_APPLICATION_CREDENTIALS needed.",
});

const userOauthVariant = productVariant({
  identifier: "googleCloudOauth",
  authLabel: "OAuth user consent",
  keywords: `${KEYWORDS} people contacts user oauth consent`,
  protocol: "http",
  defaultUrlPattern: ({ apiBaseUrl }) => {
    const url = URL.parse(apiBaseUrl || "https://people.googleapis.com/v1");
    if (
      !url ||
      url.protocol !== "https:" ||
      !/^[a-z0-9-]+\.googleapis\.com$/.test(url.hostname) ||
      [
        "www.googleapis.com",
        "oauth2.googleapis.com",
        "sts.googleapis.com",
      ].includes(url.hostname) ||
      url.port ||
      url.username ||
      url.password ||
      url.search ||
      url.hash
    ) {
      throw userFacingError(
        "Enter an HTTPS Google API service URL without a custom port, credentials, query, or fragment. Shared www, OAuth, and STS hosts aren’t supported."
      );
    }
    const base = escapeRegexLiteral(
      url.href.replace(/\/+$/, "").slice("https://".length)
    );
    return `https://(?:${base}/.*|oauth2\\.googleapis\\.com/tokeninfo$)`;
  },
  auth: oauthAuth,
  steps: [
    {
      title: "Configure Google user consent",
      items: [
        {
          kind: "markdown",
          content:
            "In the [Google Cloud Console](https://console.cloud.google.com/), select or create a project and enable the API you want to connect, such as the People API. Configure **Google Auth platform** branding, audience, and data access with only the scopes you need. Choose an external audience for personal Google accounts, and add your account as a test user if the app is in Testing. Sensitive or restricted scopes may require Google verification before wider use.",
        },
      ],
    },
    {
      title: "Create an OAuth client",
      items: [
        {
          kind: "markdown",
          content:
            "In **Google Auth platform → Clients**, create a client of type **Web application**. Add the redirect URI below as an **Authorized redirect URI**, then copy the client ID and client secret.",
        },
        {
          kind: "copyable",
          label: "Redirect URI",
          value: "{API_URL}/connected-apps/oauth/googleCloudOauth/callback",
        },
        {
          kind: "input",
          label: "Client ID",
          required: true,
          target: { kind: "oauthConnection", field: "clientId" },
        },
        {
          kind: "input",
          label: "Client secret",
          required: true,
          target: { kind: "oauthConnection", field: "clientSecret" },
        },
      ],
    },
    {
      title: "Choose API access",
      items: [
        {
          kind: "baseUrl",
          label: "API base URL",
          required: true,
          defaultValue: "https://people.googleapis.com/v1",
          placeholder: "https://people.googleapis.com/v1",
          target: { kind: "configuration", field: "apiBaseUrl" },
          helperText:
            "One HTTPS Google API service endpoint and its common API prefix. For People, use https://people.googleapis.com/v1. For all Resource Manager versions, use https://cloudresourcemanager.googleapis.com. Only this host and prefix receive credentials; shared www.googleapis.com endpoints and custom ports aren’t supported.",
        },
        {
          kind: "input",
          label: "Scopes",
          required: true,
          defaultValue: "https://www.googleapis.com/auth/contacts.readonly",
          placeholder: "https://www.googleapis.com/auth/contacts.readonly",
          target: { kind: "oauthConnection", field: "scopes" },
          helperText:
            "Space-separated scopes for the selected API. contacts.readonly is sufficient to read your People contacts; use contacts only to manage them. Replace these scopes when connecting another API. cloud-platform does not grant access to People contacts.",
        },
      ],
    },
    {
      title: "Sign in with Google",
      items: [
        {
          kind: "markdown",
          content:
            "Sign in as the Google user whose data you want to access and approve the requested permissions. This option doesn’t require a service account or domain-wide delegation.",
        },
        {
          kind: "oauth",
          dependsOn: [
            { kind: "oauthConnection", field: "clientId" },
            { kind: "oauthConnection", field: "clientSecret" },
            { kind: "configuration", field: "apiBaseUrl" },
            { kind: "oauthConnection", field: "scopes" },
          ],
        },
      ],
    },
  ],
  oauth: {
    grantType: () => "authorization_code",
    clientCredentialsFrom: "db",
    authorizationEndpoint: () => "https://accounts.google.com/o/oauth2/v2/auth",
    tokenEndpoint: () => "https://oauth2.googleapis.com/token",
    tokenEndpointAuthMethod: () => "client_secret_post",
    scopes: () => [],
    authorizationParameters: () => ({
      access_type: "offline",
      prompt: "consent",
    }),
  },
  connectionTest: {
    protocol: "http",
    request: () => ({
      url: "https://oauth2.googleapis.com/tokeninfo",
      method: "POST",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded;charset=UTF-8",
      },
      expectedStatus: 200,
      expectedBodyContains: ['"aud"', '"scope"', '"expires_in"'],
    }),
  },
  usageHint: () =>
    "Requests run as the consenting Google user and are authenticated only under the configured API base URL. Access depends on granted scopes, enabled APIs, and the user’s permissions. For People reads, include the required personFields parameter. A valid connection does not imply Cloud IAM or Workspace directory access. Reconnect if Google revokes or expires the refresh token; external apps in Testing generally have seven-day refresh tokens.",
});

export const googleCloud = defineProduct({
  name: "Google Cloud",
  tagLine: "Google Cloud Platform services",
  logo,
  tags: ["featured", "it", "engineering"],
  variants: [
    serviceAccountKeyVariant,
    vertexAiVariant,
    federationVariant,
    userOauthVariant,
  ],
});
